/**
 * 5-column opportunities kanban grouped by stage per spec section 15.8
 * and Claude Design Files README section 5.9.
 *
 * Columns: NEW, ACTIVE, AWAITING, WON, LOST. Step 1 removed drag-to-move:
 * an opportunity's stage is derived from its Current Action (set on the
 * slide-over), and WON / LOST are reached via the Mark Won / Mark Lost
 * buttons. The card is a plain clickable surface that reflows columns when
 * the action changes. Card click opens the opportunity slide-over.
 */

import { useState } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';

import { opportunityActionOrder, type OpportunityAction } from '@workspace/shared';

import { useOpportunities, type WireOpp } from '@/lib/api/sales';
import { useSlideOver } from '@/lib/slide-over';
import { OpportunityCard } from './OpportunityCard';
import { NewOpportunityDialog } from './NewOpportunityDialog';

type OppStageEnum = 'NEW' | 'ACTIVE' | 'AWAITING' | 'WON' | 'LOST';

const STAGE_COLUMNS: { stage: OppStageEnum; label: string }[] = [
  { stage: 'NEW', label: 'NEW' },
  { stage: 'ACTIVE', label: 'ACTIVE' },
  { stage: 'AWAITING', label: 'AWAITING' },
  { stage: 'WON', label: 'WON' },
  { stage: 'LOST', label: 'LOST' },
];

export function OpportunitiesBoard() {
  const { data, isLoading, isError } = useOpportunities();
  const openSlideOver = useSlideOver((s) => s.open);
  const [showNew, setShowNew] = useState(false);

  if (isLoading) return <p className="mono-label text-mx-grey-mid">LOADING…</p>;
  if (isError) return <p className="text-mx-body-sm text-mx-neg">Could not load opportunities.</p>;

  const opportunities = data?.opportunities ?? [];
  const byStage = new Map<OppStageEnum, WireOpp[]>();
  for (const col of STAGE_COLUMNS) byStage.set(col.stage, []);
  for (const opp of opportunities) byStage.get(opp.stage as OppStageEnum)?.push(opp);
  // Tabs-updates: order each column by Current Action (dropdown order),
  // budget desc as a tie-break.
  for (const arr of byStage.values()) {
    arr.sort((a, b) => {
      const order =
        opportunityActionOrder(a.currentAction as OpportunityAction) -
        opportunityActionOrder(b.currentAction as OpportunityAction);
      if (order !== 0) return order;
      return parseFloat(b.budgetFee ?? '0') - parseFloat(a.budgetFee ?? '0');
    });
  }

  return (
    <div className="flex h-[calc(100vh-12rem)] flex-col gap-mx-3">
      <div className="flex items-center justify-end">
        <button
          type="button"
          onClick={() => setShowNew(true)}
          className="flex h-9 items-center gap-mx-2 bg-mx-orange px-mx-3 text-mx-body-sm font-medium text-mx-white hover:bg-mx-orange-hover"
        >
          <PlusIcon className="h-4 w-4" strokeWidth={1.6} aria-hidden />
          New opportunity
        </button>
      </div>
      <div className="flex flex-1 overflow-x-auto border-t border-mx-grey-800">
        {STAGE_COLUMNS.map((col) => (
          <Column
            key={col.stage}
            stage={col.stage}
            label={col.label}
            opportunities={byStage.get(col.stage) ?? []}
            onCardClick={(id) => openSlideOver({ kind: 'opportunity', opportunityId: id })}
          />
        ))}
      </div>
      <NewOpportunityDialog open={showNew} onClose={() => setShowNew(false)} />
    </div>
  );
}

function Column({
  stage,
  label,
  opportunities,
  onCardClick,
}: {
  stage: OppStageEnum;
  label: string;
  opportunities: WireOpp[];
  onCardClick: (id: string) => void;
}) {
  return (
    <div
      data-testid={`opportunity-column-${stage}`}
      className="flex h-full min-w-[260px] flex-1 flex-col border-r border-mx-grey-800 bg-mx-near-black px-mx-3 py-mx-4"
    >
      <p className="mono-label mb-mx-3 text-mx-grey-300">
        {label} <span className="text-mx-grey-mid">({opportunities.length})</span>
      </p>
      <ul className="flex flex-1 flex-col gap-mx-2 overflow-y-auto">
        {opportunities.map((opp) => (
          <li key={opp.id}>
            <OpportunityCard opportunity={opp} onClick={onCardClick} axis="stage" />
          </li>
        ))}
      </ul>
    </div>
  );
}
